import { useMemo, useState } from 'react';
import { Link } from 'react-router-dom';
import { useIssueList } from '@/hooks/useIssueMemory';
import { IssueStatus, IssueSummary } from '@/api/issue-memory.api';
import { Card, CardContent, CardHeader, CardTitle } from '@/components/ui/card';
import { Input } from '@/components/ui/input';
import { LoadingSpinner } from '@/components/common/LoadingSpinner';
import { AlertTriangle, ExternalLink, GitMerge, Hourglass, MessageCircleQuestion, Search, User } from 'lucide-react';
import { cn, formatRelativeTime } from '@/utils/helpers';

/**
 * Board: every open issue across projects by canonical status, who owns it,
 * what it waits on and how long it has been quiet (plan §9). Work flows left to
 * right: in progress, AI QA (test), the owner's check (in review), done. Done shows
 * only the latest closed issues; the rest are in GitLab.
 */

const ATTENTION: IssueStatus[] = ['blocked', 'needs-info', 'needs-evidence'];

const COLUMNS: Array<{ key: string; title: string; statuses: IssueStatus[]; tone: string }> = [
  { key: 'attention', title: 'Needs attention', statuses: ATTENTION, tone: 'border-t-red-500' },
  { key: 'todo', title: 'To do', statuses: ['todo'], tone: 'border-t-slate-400' },
  { key: 'in-progress', title: 'In progress', statuses: ['in-progress'], tone: 'border-t-blue-500' },
  { key: 'test', title: 'Test', statuses: ['test'], tone: 'border-t-amber-500' },
  { key: 'in-review', title: 'In review', statuses: ['in-review'], tone: 'border-t-violet-500' },
];

const DONE_SHOWN = 10;

/** A project's closed issues in GitLab, from one of its issue links */
function closedIssuesUrl(issue: IssueSummary): string | undefined {
  const project = issue.webUrl?.split('/-/')[0];
  return project ? `${project}/-/issues?state=closed&sort=closed_at_desc` : undefined;
}

const DAY = 24 * 60 * 60 * 1000;

function quietDays(issue: IssueSummary): number | null {
  if (!issue.lastHumanActivityAt) return null;
  return Math.floor((Date.now() - new Date(issue.lastHumanActivityAt).getTime()) / DAY);
}

function projectName(issue: IssueSummary): string {
  return issue.projectPath?.split('/').pop() || `project ${issue.gitlabProjectId}`;
}

function IssueCard({ issue }: { issue: IssueSummary }) {
  const quiet = quietDays(issue);
  const openMr = issue.mrs.find((m) => m.state === 'opened') || issue.mrs[issue.mrs.length - 1];
  return (
    <Link
      to={`/issues/${issue.gitlabProjectId}/${issue.iid}`}
      className="block rounded-md border bg-background p-3 hover:border-primary/50 transition-colors"
    >
      <div className="flex items-start justify-between gap-2">
        <span className="text-xs font-mono text-muted-foreground">{issue.ref}</span>
        <span className="flex gap-1">
          {issue.atRisk && (
            <span className="rounded bg-red-600 px-1.5 py-0.5 text-[10px] uppercase tracking-wide text-white">at risk</span>
          )}
          {issue.dod && !issue.dod.passed && (
            <span className="rounded bg-amber-100 px-1.5 py-0.5 text-[10px] uppercase tracking-wide text-amber-800">reopened by DoD</span>
          )}
          {issue.labels.includes('qa-approved') && (
            <span className="rounded bg-green-100 px-1.5 py-0.5 text-[10px] uppercase tracking-wide text-green-800">QA approved</span>
          )}
          {ATTENTION.includes(issue.status) && (
            <span className="rounded bg-red-100 px-1.5 py-0.5 text-[10px] uppercase tracking-wide text-red-700">{issue.status}</span>
          )}
        </span>
      </div>
      <p dir="auto" className="mt-1 text-sm font-medium leading-snug">
        {issue.title}
      </p>
      <div className="mt-2 flex flex-wrap items-center gap-x-3 gap-y-1 text-xs text-muted-foreground">
        <span className="flex items-center gap-1">
          <User className="h-3 w-3" />
          {issue.assignees.length ? issue.assignees.map((a) => `@${a}`).join(', ') : 'unassigned'}
        </span>
        {openMr && (
          <span className="flex items-center gap-1">
            <GitMerge className="h-3 w-3" />!{openMr.iid}
            {openMr.draft ? ' draft' : openMr.state && openMr.state !== 'opened' ? ` ${openMr.state}` : ''}
            {openMr.pipeline && (
              <span className={openMr.pipeline === 'success' ? 'text-green-600' : openMr.pipeline === 'failed' ? 'text-red-600' : ''}>
                · {openMr.pipeline}
              </span>
            )}
          </span>
        )}
        {quiet !== null && quiet >= 2 && (
          <span className={cn('flex items-center gap-1', quiet >= 5 ? 'text-red-600' : 'text-amber-600')}>
            <Hourglass className="h-3 w-3" />
            quiet {quiet}d
          </span>
        )}
      </div>
      {issue.waitingOn?.username && (
        <p className="mt-2 flex items-center gap-1 text-xs text-amber-700">
          <MessageCircleQuestion className="h-3 w-3" />
          waiting on @{issue.waitingOn.username} since {formatRelativeTime(issue.waitingOn.since)}
        </p>
      )}
    </Link>
  );
}

function PeopleSummary({ issues, onPick, selected }: { issues: IssueSummary[]; onPick: (u: string) => void; selected: string }) {
  const people = useMemo(() => {
    const map = new Map<string, { open: number; inProgress: number; review: number; waitingOnThem: number }>();
    const get = (u: string) => {
      if (!map.has(u)) map.set(u, { open: 0, inProgress: 0, review: 0, waitingOnThem: 0 });
      return map.get(u)!;
    };
    for (const issue of issues) {
      for (const a of issue.assignees) {
        const p = get(a);
        p.open++;
        if (issue.status === 'in-progress') p.inProgress++;
        if (issue.status === 'in-review' || issue.status === 'test') p.review++;
      }
      if (issue.waitingOn?.username) get(issue.waitingOn.username).waitingOnThem++;
    }
    return [...map.entries()].sort((a, b) => b[1].open - a[1].open);
  }, [issues]);

  if (!people.length) return null;
  return (
    <Card>
      <CardHeader className="pb-2">
        <CardTitle className="text-base">Who is doing what</CardTitle>
      </CardHeader>
      <CardContent className="flex flex-wrap gap-2">
        {people.map(([user, p]) => (
          <button
            key={user}
            onClick={() => onPick(selected === user ? '' : user)}
            className={cn(
              'rounded-md border px-3 py-2 text-left text-xs hover:border-primary/50',
              selected === user && 'border-primary bg-primary/5'
            )}
          >
            <div className="font-medium text-sm">@{user}</div>
            <div className="text-muted-foreground">
              {p.open} open · {p.inProgress} in progress · {p.review} in review/test
            </div>
            {p.waitingOnThem > 0 && <div className="text-amber-700">{p.waitingOnThem} waiting on them</div>}
          </button>
        ))}
      </CardContent>
    </Card>
  );
}

export default function Board() {
  const [project, setProject] = useState('');
  const [person, setPerson] = useState('');
  const [search, setSearch] = useState('');
  const { data: issues = [], isLoading, error } = useIssueList({ state: 'opened', limit: 300 });
  // Newest first; enough to fill the column after the filters
  const { data: closed = [] } = useIssueList({ state: 'closed', limit: 100 });

  const projects = useMemo(
    () => [...new Set(issues.map(projectName))].sort((a, b) => a.localeCompare(b)),
    [issues]
  );

  const matches = useMemo(() => {
    const q = search.trim().toLowerCase();
    return (i: IssueSummary) =>
      (!project || projectName(i) === project) &&
      (!person || i.assignees.includes(person) || i.waitingOn?.username === person) &&
      (!q || i.title.toLowerCase().includes(q) || i.ref.toLowerCase().includes(q));
  }, [project, person, search]);
  const filtered = useMemo(() => issues.filter(matches), [issues, matches]);
  const done = useMemo(() => closed.filter(matches), [closed, matches]);
  // One "more in GitLab" link per project in the column (GitLab has no closed-issues page across projects)
  const doneLinks = useMemo(() => {
    const links = new Map<string, string>();
    for (const i of done.slice(0, DONE_SHOWN)) {
      const url = closedIssuesUrl(i);
      if (url && !links.has(projectName(i))) links.set(projectName(i), url);
    }
    return [...links];
  }, [done]);

  const waiting = filtered.filter((i) => i.waitingOn?.username).length;
  const quiet = filtered.filter((i) => (quietDays(i) ?? 0) >= 5).length;

  if (isLoading) return <LoadingSpinner className="py-24" />;
  if (error) return <p className="text-sm text-red-600">Could not load the board: {(error as Error).message}</p>;

  return (
    <div className="space-y-6">
      <div className="flex flex-wrap items-end justify-between gap-4">
        <div>
          <h1 className="text-2xl font-bold">Board</h1>
          <p className="text-sm text-muted-foreground">
            {filtered.length} open issues · {waiting} waiting on someone · {quiet} quiet for 5+ days
          </p>
        </div>
        <div className="flex flex-wrap gap-2">
          <select
            value={project}
            onChange={(e) => setProject(e.target.value)}
            className="h-10 rounded-md border border-input bg-background px-3 text-sm"
          >
            <option value="">All projects</option>
            {projects.map((p) => (
              <option key={p} value={p}>
                {p}
              </option>
            ))}
          </select>
          <div className="relative">
            <Search className="absolute left-2.5 top-3 h-4 w-4 text-muted-foreground" />
            <Input value={search} onChange={(e) => setSearch(e.target.value)} placeholder="Search" className="w-56 pl-8" />
          </div>
        </div>
      </div>

      <PeopleSummary issues={issues.filter((i) => !project || projectName(i) === project)} onPick={setPerson} selected={person} />

      {issues.length === 0 ? (
        <Card>
          <CardContent className="flex items-center gap-2 p-6 text-sm text-muted-foreground">
            <AlertTriangle className="h-4 w-4" />
            No issue memory yet. Run the import (POST /api/issue-memory/backfill) or wait for GitLab events.
          </CardContent>
        </Card>
      ) : (
        <div className="grid gap-4 md:grid-cols-3 xl:grid-cols-6">
          {COLUMNS.map((col) => {
            const items = filtered
              .filter((i) => col.statuses.includes(i.status))
              .sort((a, b) => (quietDays(b) ?? 0) - (quietDays(a) ?? 0));
            return (
              <div key={col.key} className={cn('rounded-lg border border-t-4 bg-muted/30', col.tone)}>
                <div className="flex items-center justify-between px-3 py-2">
                  <h2 className="text-sm font-semibold">{col.title}</h2>
                  <span className="text-xs text-muted-foreground">{items.length}</span>
                </div>
                <div className="space-y-2 px-2 pb-2">
                  {items.map((issue) => (
                    <IssueCard key={`${issue.gitlabProjectId}-${issue.iid}`} issue={issue} />
                  ))}
                </div>
              </div>
            );
          })}
          <div className="rounded-lg border border-t-4 border-t-green-600 bg-muted/30">
            <div className="flex items-center justify-between px-3 py-2">
              <h2 className="text-sm font-semibold">Done</h2>
              <span className="text-xs text-muted-foreground" title="The latest closed issues">
                last {Math.min(done.length, DONE_SHOWN)}
              </span>
            </div>
            <div className="space-y-2 px-2 pb-2">
              {done.slice(0, DONE_SHOWN).map((issue) => (
                <IssueCard key={`${issue.gitlabProjectId}-${issue.iid}`} issue={issue} />
              ))}
              {doneLinks.map(([name, url]) => (
                <a
                  key={name}
                  href={url}
                  target="_blank"
                  rel="noreferrer"
                  className="flex items-center justify-center gap-1 rounded-md border border-dashed px-3 py-2 text-xs text-muted-foreground hover:border-primary/50 hover:text-foreground"
                >
                  More closed issues{doneLinks.length > 1 ? ` of ${name}` : ''} in GitLab <ExternalLink className="h-3 w-3" />
                </a>
              ))}
            </div>
          </div>
        </div>
      )}
    </div>
  );
}
